// comments/CommentItem.js
import { memo, useRef, useCallback, useMemo, useState, useEffect } from "react";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartOutline } from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import DropdownMenuPortal from "../ui/DropdownMenuPortal";
import ConfirmModal from "../ui/ConfirmModal";
import ReportModal from "../ui/ReportModal";
import EditCommentModal from "./notes/EditCommentModal";
import { useCommentLike } from "../hooks/useCommentLike";
import { useTimeAgo } from "../hooks/useTimeAgo";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { getCommentMenuItems } from "../utils/getMenuItems";
import { api } from "../api";
import { logError } from "../utils/logger";
import { Avatar } from "./avatars/Avatar";

/**
 * Memo returns a memoized version of the component.
 * It skips re-rendering when the props have not changed
 * A comment list can be long. Without Memo,
 * every parent re-render would re-render every comment item
 */
const CommentItem = memo(function CommentItem({
  c,
  noteId,
  noteOwnerId,
  onOpenMenu,
  onCloseMenu,
  openCommentMenuId,
  boundaryRef,
  scrollRef,
  onCommentDelete,
  isHighlighted = false, // whether this comment should be highlighted
}) {
  const btnRef = useRef(null);
  const commentRef = useRef(null); // ref for scrolling to this comment
  const isOpen = openCommentMenuId === c.id;

  // State to control highlight fade animation
  const [showHighlight, setShowHighlight] = useState(isHighlighted);

  // Get current user
  const { user } = useAuth();
  const { toast } = useToast();

  // Auto-scroll to highlighted comment and fade out highlight after 3 seconds
  useEffect(() => {
    if (isHighlighted && commentRef.current) {
      // Small delay to ensure comment is rendered
      const scrollTimer = setTimeout(() => {
        commentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);

      // Fade out highlight after 3 seconds
      const fadeTimer = setTimeout(() => {
        setShowHighlight(false);
      }, 500);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(fadeTimer);
      };
    }
  }, [isHighlighted]);

  // Confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [localCommentText, setLocalCommentText] = useState(c.text);
  const [localEditedAt, setLocalEditedAt] = useState(c.editedAt);

  // Use the comment like hook for backend-synced like functionality
  const {
    liked: isLiked,
    likeCount,
    toggleLike: toggleCommentLike,
  } = useCommentLike(noteId, c.id, c.isLikedByCurrentUser, c.likes);

  // Auto-updating time display
  const { display: timeAgo, fullDateTime } = useTimeAgo(c.createdAt);

  // Determine ownership for context-aware menu
  const isCommentOwner = c.user.id === user?.id;
  const isNoteOwner = noteOwnerId === user?.id;

  /**
   * Handle comment deletion
   */
  const handleDeleteComment = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteComment = useCallback(async () => {
    try {
      await onCommentDelete?.(c.id);
    } catch (err) {
      logError("Failed to delete comment:", err);
      toast.error("Failed to delete comment");
    }
  }, [c.id, onCommentDelete, toast]);

  /**
   * Handle comment reporting
   */
  const handleReportComment = useCallback(() => {
    setShowReportModal(true);
  }, []);

  const handleSubmitReport = useCallback(
    async (reason, details) => {
      try {
        setSubmittingReport(true);
        await api.reportComment(noteId, c.id, reason, details);
        setShowReportModal(false);
        toast.success(
          "Report submitted. Thank you for helping keep Traces safe.",
          3000,
        );
      } catch (err) {
        logError("Failed to report comment:", err);
        toast.error("Failed to submit report", 3000);
      } finally {
        setSubmittingReport(false);
      }
    },
    [noteId, c.id, toast],
  );

  /**
   * Handle comment editing
   */
  const handleEditComment = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleUpdateComment = useCallback(
    async (updates) => {
      try {
        const response = await api.updateComment(noteId, c.id, {
          text: updates.text,
        });
        setLocalCommentText(updates.text); // Update local text
        setLocalEditedAt(response.data.editedAt); // Update editedAt timestamp
        setShowEditModal(false);
        toast.success("Comment updated successfully", 3000);
      } catch (err) {
        logError("Failed to update comment:", err);
        toast.error("Failed to update comment", 3000);
      }
    },
    [noteId, c.id, toast],
  );

  const commentMenuItems = useMemo(
    () =>
      getCommentMenuItems(isCommentOwner, isNoteOwner, {
        onEdit: handleEditComment,
        onDelete: handleDeleteComment,
        onReport: handleReportComment,
      }),
    [
      isCommentOwner,
      isNoteOwner,
      handleEditComment,
      handleDeleteComment,
      handleReportComment,
    ],
  );

  const openMenu = useCallback(() => onOpenMenu(c.id), [c.id, onOpenMenu]);

  return (
    <li
      ref={commentRef}
      className={`px-3 py-2 rounded-xl border transition-all duration-1000 ${
        showHighlight
          ? "bg-primary-700 border-border-light text-text-inverse"
          : "bg-surface-elevated border-border-light"
      }`}
    >
      {/**
       * flex: makes the comment container a flex-row container
       *
       */}
      <div className="flex gap-2">
        {/* Avatar */}
        <Avatar
          name={c.user.name}
          avatarUrl={c.user.avatar}
          size="sm"
        />

        {/* Content */}
        {/**
         * flex-1: Grow/shrink to fill the available space (By default, flex items only take up space that fits their content.)
         *    Flex items will only shrink as small as the shortest unbroken word in their container
         * min-w-0: Overrides Flex's default shrinking behaviour
         *    Allows the flex container to shrink as far as width 0
         */}
        <div className="min-w-0 flex-1">
          {/* Name + meta */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary truncate">
              {c.user.name}
            </span>
            <span className="text-xs text-text-muted" title={fullDateTime}>
              {timeAgo}
              {localEditedAt && (
                <span className="text-text-muted ml-1">(edited)</span>
              )}
            </span>
          </div>

          {/* Text */}
          <p className="mt-0.5 text-sm leading-5 text-text-primary whitespace-pre-line">
            {localCommentText}
          </p>

          {/* Likes count */}
          <div className="mt-1 text-xs text-text-secondary">
            <span className={`font-medium text-text-primary'}`}>
              {likeCount}
            </span>{" "}
            likes
          </div>
        </div>

        {/* Right column: ellipsis + heart */}
        {/**
         * flex, flex-col: makes the container a flex container arranged vertically (column manner)
         * items-center: center the children horizontally (left to right)
         * gap-1: leave a gap between children
         */}
        <div className="flex flex-col items-center gap-1">
          {/* Ellipsis menu button */}
          <div className="relative">
            <button
              ref={btnRef}
              className="p-1 rounded-full hover:bg-surface-sunken"
              aria-label="More options"
              onClick={isOpen ? onCloseMenu : openMenu}
            >
              <EllipsisHorizontalIcon className="h-4 w-4 text-primary" />
            </button>
            {isOpen && (
              <DropdownMenuPortal
                anchorRef={btnRef}
                boundaryRef={boundaryRef}
                scrollRefs={[boundaryRef, scrollRef]}
                onClose={onCloseMenu}
                items={commentMenuItems}
                side="bottom"
                align="end"
              />
            )}
          </div>

          {/* Like button */}
          <button
            onClick={toggleCommentLike}
            className="inline-flex items-center justify-center rounded-full h-7 w-7 transition-transform duration-150 active:scale-95"
            aria-pressed={isLiked}
            aria-label="Like comment"
          >
            {isLiked ? (
              <HeartSolid className="h-4 w-4 text-destructive" />
            ) : (
              <HeartOutline className="h-4 w-4 text-text-secondary" />
            )}
          </button>
        </div>
      </div>

      {/* Delete Comment Confirmation Modal */}
      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteComment}
        title="Delete comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />

      {/* Report Comment Modal */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
        type="comment"
        submitting={submittingReport}
      />

      {/* Edit Comment Modal */}
      {showEditModal && (
        <EditCommentModal
          comment={{ ...c, text: localCommentText }}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateComment}
        />
      )}
    </li>
  );
});

export default CommentItem;
